/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    this.form = this.container.querySelector('form.searchform');
    this.button = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');
    this.cache = new Map();
    this.controller = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.button.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        if (OverlayManager.isOpen(this.container)) {
          OverlayManager.close(this.container);
        } else {
          this.open();
        }
      });
    });

    this.predictiveSearchResults.addEventListener('click', (event) => {
      const submitButton = event.target.closest('#search-results-submit');
      if (submitButton) {
        this.form.submit();
      }
    });

    this.clearButton = this.container.querySelector('[data-search-clear]');
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.syncClearButton();
        this.onChange();
        this.input.focus();
      });
      this.input.addEventListener('input', () => this.syncClearButton());
      this.syncClearButton();
    }

    // ArrowDown from the field walks into the results and ArrowUp walks back out, which is
    // what every search field people use all day does. Before this the keys did nothing at
    // all: focus sat in the input while ten results waited below it (SRC-001).
    this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
    this.predictiveSearchResults.addEventListener('keydown', (event) => this.onResultsKeydown(event));
  }

  open() {
    this.stampPromotionTemplate();
    OverlayManager.open(this.container);
    setTimeout(() => {
      this.input.focus({
        preventScroll: true
      });
    }, 100);
    dispatchCustomEvent('search:open');
  }

  syncClearButton() {
    if (!this.clearButton) return;
    this.clearButton.hidden = this.getQuery().length === 0;
  }

  /** Every result a visitor can move focus to, in the order they are shown. */
  resultLinks() {
    return [...this.predictiveSearchResults.querySelectorAll('a[href], #search-results-submit')]
      .filter((el) => el.getClientRects().length > 0);
  }

  onInputKeydown(event) {
    if (event.key !== 'ArrowDown') return;
    const links = this.resultLinks();
    if (!links.length) return;
    event.preventDefault();
    links[0].focus();
  }

  onResultsKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const links = this.resultLinks();
    const index = links.indexOf(document.activeElement);
    if (index === -1) return;
    event.preventDefault();

    if (event.key === 'ArrowUp' && index === 0) {
      // Back to the field rather than wrapping to the bottom: the field is where the query
      // is, and someone arrowing up off the first result is going back to edit it.
      this.input.focus();
      return;
    }
    const next = event.key === 'ArrowDown'
      ? Math.min(index + 1, links.length - 1)
      : index - 1;
    links[next].focus();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.predictiveSearchResults.classList.remove('active');
      return;
    }
    this.predictiveSearchResults.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  getSearchResults(searchTerm) {
    if (this.controller) {
      this.controller.abort();
    }
    this.controller = new AbortController();

    // Serve from cache if available
    if (this.cache.has(searchTerm)) {
      this.renderSearchResults(this.cache.get(searchTerm));
      return;
    }

    this.predictiveSearchResults.classList.add('loading');

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,article,query',
      'resources[limit]': '10',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
      section_id: 'predictive-search'
    });
    /* The products a search names, 1 to 4 of them, go first even when sold out (merchant, 5 Oct
       2026; snippets/ktechs-search-name-matches). Shopify puts sold-out products last, so the
       search page's names view (templates/search.names.liquid) is asked at the same time, with
       them in place, and the ones it names lead the list. */
    const namesUrl = new URL(theme.routes.search_url, window.location.origin);
    namesUrl.search = new URLSearchParams({
      q: searchTerm,
      type: 'product',
      'options[unavailable_products]': 'show',
      'options[fields]': 'title',
      view: 'names'
    }).toString();
    const signal = this.controller.signal;
    const getText = (url) =>
      fetch(url, { signal }).then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      });

    Promise.all([
      getText(`${theme.routes.predictive_search_url}?${params.toString()}`),
      getText(namesUrl.toString()).catch((error) => {
        if (error.name === 'AbortError') throw error;
        return null;
      })
    ])
      .then(([text, namedText]) => {
        this.predictiveSearchResults.classList.remove('loading');
        const results = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (namedText) {
          this.pinNamedProducts(results, new DOMParser().parseFromString(namedText, 'text/html'));
        }
        const resultsMarkup = results.innerHTML;

        // Cap cache size
        if (this.cache.size >= 50) {
          const firstKey = this.cache.keys().next().value;
          this.cache.delete(firstKey);
        }
        this.cache.set(searchTerm, resultsMarkup);

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        this.predictiveSearchResults.classList.remove('loading');
        this.predictiveSearchResults.classList.remove('active');
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
  }

  pinNamedProducts(results, namedDocument) {
    const named = [...namedDocument.querySelectorAll('#search-names > template')].map((template) => template.content.querySelector('product-card')).filter(Boolean);
    const panel = [...results.querySelectorAll('.side-panel-content--tab-panel')].find((tab) => tab.querySelector('product-card'));
    if (!named.length || !panel) return;
    const handles = new Set(named.map((card) => card.dataset.handle));
    panel.querySelectorAll('product-card[data-handle]').forEach((card) => {
      if (handles.has(card.dataset.handle)) card.remove();
    });
    panel.prepend(...named.map((card) => results.ownerDocument.importNode(card, true)));
    [...panel.querySelectorAll('product-card')].slice(10).forEach((card) => card.remove());
  }

  stampPromotionTemplate() {
    const tpl = this.container.querySelector('.thb-predictive-search--promotion-template');
    if (tpl) {
      tpl.replaceWith(tpl.content.cloneNode(true));
    }
  }
}
/* Wired the moment this script runs, straight after app.js (which owns OverlayManager) at the end of
   the body, with the panel's markup already parsed. It used to wait for window load, which fires only after
   every image and app script has finished, so for that whole stretch the header's search icon was a
   plain link: measured on a phone on a mobile connection, a product page painted at 1.28s and
   loaded at 3.98s, and a tap in between left for /search instead of opening this panel.

   A tap that lands before even this point is held by the listener in layout/theme.liquid and
   opened here, so the icon does the same thing every time. */
if (typeof PredictiveSearch !== 'undefined') {
  const early = window.ktechsEarlySearch;
  let search = null;
  try {
    search = new PredictiveSearch();
  } finally {
    // Released even if the panel could not be wired: a held tap then goes where the link points.
    if (early) {
      early.ready = true;
      document.querySelectorAll('.thb-quick-search[aria-busy]').forEach((link) => link.removeAttribute('aria-busy'));
      if (early.pending) {
        if (search) search.open();
        else window.location.href = early.pending;
      }
    }
  }
}
